// Typed wrappers for the wave-2 control-plane endpoints (providers, model targets, agent
// profiles, routing, Maestro, quotas, runtime, supervision, observability, assistant and
// infrastructure). Paths and bodies mirror apps/api/src/routes/*.ts exactly.
import type { AcceptedOperation } from "@maestros/contracts";
import { api, buildUrl } from "./client.ts";
import type {
  AgentProfile,
  AssistantCommand,
  AssistantHistoryResponse,
  AssistantMessageResponse,
  AssistantSessionsResponse,
  AttemptBackend,
  AttemptsPage,
  CoolifySecretField,
  EffectivePreview,
  PatchCoolifyInput,
  PatchServerInput,
  ServerSecretField,
  CatalogResponse,
  ConnectionTestKind,
  CoolifyConnection,
  CreateCoolifyInput,
  CreateServerInput,
  EffectiveConfigView,
  GauntletResponse,
  InfraServer,
  LogsQuery,
  LogsResponse,
  MaestroPutResult,
  MaestroReviewsResponse,
  MaestroView,
  ModelTarget,
  ModelTargetDetail,
  ModelTargetPatchResult,
  ProviderConnection,
  ProviderConnectionDetail,
  QuotaPoolInput,
  QuotaPoolView,
  RoutingConfigLayer,
  RoutingConfigRecord,
  RuntimeActionAccepted,
  RuntimeServiceView,
  TraceResponse,
  UsageResponse, AlertView, BottleneckReport, RetentionView, ReviewModeReport } from "./types-wave2.ts";

const enc = encodeURIComponent;
const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {});

// ---------- Provider connections ----------

export interface ConnectionInput {
  name: string;
  adapter: ProviderConnection["adapter"];
  base_url: string;
  auth_mode: ProviderConnection["auth_mode"];
  quota_pool_id: string | null;
  /** Adapter `integration_module` (D20): the `ia` module instance holding the key; null for every other adapter. */
  module_instance_id: string | null;
  /** D29: the model kinds this connection may serve (at least one; the API defaults to every kind). */
  allowed_kinds: ProviderConnection["allowed_kinds"];
}

export const listConnections = (signal?: AbortSignal) => api<{ items: ProviderConnection[] }>("/provider-connections", withSignal(signal));
export const getConnection = (id: string, signal?: AbortSignal) => api<ProviderConnectionDetail>(`/provider-connections/${enc(id)}`, withSignal(signal));
export const createConnection = (body: ConnectionInput) => api<ProviderConnection>("/provider-connections", { method: "POST", body });
export const updateConnection = (id: string, body: Partial<ConnectionInput> & { expected_version: number; state?: "draft" | "disabled" }) =>
  api<ProviderConnection>(`/provider-connections/${enc(id)}`, { method: "PATCH", body });
/** Write-only: the value is sent once and never read back. */
export const putConnectionSecret = (id: string, value: string) => api<ProviderConnection>(`/provider-connections/${enc(id)}/secret`, { method: "PUT", body: { value } });
export const revokeConnectionSecret = (id: string) => api<ProviderConnection>(`/provider-connections/${enc(id)}/secret`, { method: "DELETE" });
export const runConnectionTest = (id: string, body: { kind: ConnectionTestKind; model_target_id?: string; max_cost_cents?: number }) =>
  api<AcceptedOperation>(`/provider-connections/${enc(id)}/tests`, { method: "POST", body });
export const getCatalog = (id: string) => api<CatalogResponse>(`/provider-connections/${enc(id)}/models`);
export const providerConfigExportUrl = () => buildUrl("/provider-config/export");

// ---------- Model targets ----------

export interface ModelTargetInput {
  connection_id: string;
  external_model_id: string;
  display_name: string;
  kind: ModelTarget["kind"];
  protocol: ModelTarget["protocol"];
  declared_capabilities: ModelTarget["declared_capabilities"];
  context_window: number | null;
  max_output_tokens: number | null;
  pricing: { currency: string; input_per_mtok: number | null; output_per_mtok: number | null; valid_from: string; source: string } | null;
  is_free: boolean | null;
  strong: boolean;
  quota_pool_id: string | null;
  alias: string | null;
}

export const listModelTargets = (signal?: AbortSignal) => api<{ items: ModelTarget[] }>("/model-targets", withSignal(signal));
export const getModelTarget = (id: string) => api<ModelTargetDetail>(`/model-targets/${enc(id)}`);
export const createModelTarget = (body: ModelTargetInput) => api<ModelTarget>("/model-targets", { method: "POST", body });
export const updateModelTarget = (id: string, body: Partial<Omit<ModelTargetInput, "connection_id">> & { expected_version: number; state?: "unverified" | "disabled" }) =>
  api<ModelTargetPatchResult>(`/model-targets/${enc(id)}`, { method: "PATCH", body });
export const probeModelTarget = (id: string, kind: "capability" | "conformance", maxCostCents: number) =>
  api<AcceptedOperation>(`/model-targets/${enc(id)}/${kind === "capability" ? "capability-probes" : "backend-conformance"}`, {
    method: "POST",
    body: { max_cost_cents: maxCostCents },
  });

// ---------- Agent profiles ----------

export interface AgentProfileInput {
  project_id: string | null;
  name: string;
  role: AgentProfile["role"];
  instructions: string;
  model_target_id: string | null;
  fallback_model_target_ids: string[];
  tools: string[];
  context_limit: number | null;
  output_limit: number | null;
  budget: { max_cost_cents_per_attempt: number | null; max_calls_per_attempt: number | null };
  concurrency: number;
  review_policy: { reviewer: "maestro" | "critic" | "none" };
}

export const listAgentProfiles = (projectId?: string | null, signal?: AbortSignal) =>
  api<{ items: AgentProfile[] }>("/agent-profiles", { query: { project_id: projectId ?? undefined }, ...withSignal(signal) });
export const createAgentProfile = (body: AgentProfileInput) => api<AgentProfile>("/agent-profiles", { method: "POST", body });
export const updateAgentProfile = (id: string, body: Partial<Omit<AgentProfileInput, "project_id" | "role">> & { expected_version: number; state?: "active" | "disabled" }) =>
  api<AgentProfile>(`/agent-profiles/${enc(id)}`, { method: "PATCH", body });

// ---------- Routing configurations ----------

export const listRoutingConfigs = (projectId?: string | null, signal?: AbortSignal) =>
  api<{ items: RoutingConfigRecord[] }>("/routing-configs", { query: { project_id: projectId ?? undefined }, ...withSignal(signal) });
export const createRoutingDraft = (projectId: string | null, body: RoutingConfigLayer) =>
  api<RoutingConfigRecord>("/routing-configs", { method: "POST", body: { project_id: projectId, body } });
export const validateRoutingConfig = (id: string) => api<RoutingConfigRecord>(`/routing-configs/${enc(id)}/validate`, { method: "POST", body: {} });
/** `expectedVersion` is the version currently active in the scope (0 when none). */
export const activateRoutingConfig = (id: string, expectedVersion: number) =>
  api<RoutingConfigRecord>(`/routing-configs/${enc(id)}/activate`, { method: "POST", body: { expected_version: expectedVersion } });

export const getEffectiveConfig = (projectId: string, signal?: AbortSignal) => api<EffectiveConfigView>(`/projects/${enc(projectId)}/effective-config`, withSignal(signal));
export const getMaestro = (projectId: string, signal?: AbortSignal) => api<MaestroView>(`/projects/${enc(projectId)}/maestro`, withSignal(signal));
export const putMaestro = (projectId: string, body: { model_target_id: string; substitute_model_target_ids: string[]; expected_version: number }) =>
  api<MaestroPutResult>(`/projects/${enc(projectId)}/maestro`, { method: "PUT", body });

/** Read-only preview of the effective table if this config were active (global drafts need a project). */
export const getEffectivePreview = (configId: string, projectId: string | null, signal?: AbortSignal) =>
  api<EffectivePreview>(`/routing-configs/${enc(configId)}/effective-preview`, { query: { project_id: projectId ?? undefined }, ...withSignal(signal) });

// ---------- Quota pools ----------

export const listQuotaPools = (signal?: AbortSignal) => api<{ items: QuotaPoolView[]; generated_at: string }>("/quota-pools", withSignal(signal));
export const createQuotaPool = (body: QuotaPoolInput) => api<QuotaPoolView>("/quota-pools", { method: "POST", body });
export const updateQuotaPool = (id: string, body: Partial<QuotaPoolInput> & { expected_version: number }) =>
  api<QuotaPoolView>(`/quota-pools/${enc(id)}`, { method: "PATCH", body });

// ---------- Runtime (RF-OPS-07) ----------

export const listRuntimeServices = (signal?: AbortSignal) => api<{ items: RuntimeServiceView[]; generated_at: string }>("/runtime/services", withSignal(signal));
export const listAlerts = (state: "active" | "recovered", signal?: AbortSignal) => api<{ items: AlertView[]; generated_at: string }>("/alerts", { query: { state }, ...withSignal(signal) });
export const getBottleneckReport = (projectId: string, q: { before_from: string; before_to: string; after_from: string; after_to: string; group_by: string }, signal?: AbortSignal) =>
  api<BottleneckReport>(`/projects/${enc(projectId)}/reports/bottlenecks`, { query: q, ...withSignal(signal) });
export const getReviewModeReport = (projectId: string, q: { from: string; to: string }, signal?: AbortSignal) =>
  api<ReviewModeReport>(`/projects/${enc(projectId)}/reports/review-modes`, { query: q, ...withSignal(signal) });
export const getRetention = (signal?: AbortSignal) => api<RetentionView>("/settings/retention", withSignal(signal));
export const putRetention = (body: { detailed_logs_days: number; expected_version: number }) => api<RetentionView>("/settings/retention", { method: "PUT", body });
export const acknowledgeAlert = (id: string) => api<AlertView>(`/alerts/${encodeURIComponent(id)}/acknowledge`, { method: "POST", body: {} });
export const requestRuntimeAction = (body: { service_id: string; action: "drain" | "resume" | "restart"; expected_version: number; reason?: string }) =>
  api<RuntimeActionAccepted>("/runtime/actions", { method: "POST", body });

// ---------- Supervision / observability ----------

/** GET /v1/attempts: attempts across the caller's projects, newest first, cursor-paginated. */
export const listAttempts = (q: { project_id?: string | null; state?: string | null; cursor?: string | null; limit?: number }, signal?: AbortSignal) =>
  api<AttemptsPage>("/attempts", { query: { project_id: q.project_id ?? undefined, state: q.state ?? undefined, cursor: q.cursor ?? undefined, limit: q.limit ?? 50 }, ...withSignal(signal) });

export const getTrace = (taskId: string, signal?: AbortSignal) => api<TraceResponse>(`/tasks/${enc(taskId)}/trace`, withSignal(signal));
export const getGauntlet = (taskId: string, signal?: AbortSignal) => api<GauntletResponse>(`/tasks/${enc(taskId)}/gauntlet`, withSignal(signal));
export const getMaestroReviews = (taskId: string, signal?: AbortSignal) => api<MaestroReviewsResponse>(`/tasks/${enc(taskId)}/maestro-reviews`, withSignal(signal));
export const getAttemptBackend = (attemptId: string, signal?: AbortSignal) => api<AttemptBackend>(`/attempts/${enc(attemptId)}/backend`, withSignal(signal));
export const getUsage = (projectId: string, signal?: AbortSignal) => api<UsageResponse>(`/projects/${enc(projectId)}/usage`, withSignal(signal));

function logsQuery(q: LogsQuery): Record<string, string | number | undefined> {
  return {
    level: q.level || undefined,
    service: q.service || undefined,
    code: q.code || undefined,
    task_id: q.task_id || undefined,
    search: q.search || undefined,
    cursor: q.cursor ?? undefined,
    limit: q.limit ?? 50,
  };
}

export const getLogs = (projectId: string, q: LogsQuery, signal?: AbortSignal) =>
  api<LogsResponse>(`/projects/${enc(projectId)}/logs`, { query: logsQuery(q), ...withSignal(signal) });
/** Same filters as the viewer; downloaded by the browser with the session cookie. */
export const logsExportUrl = (projectId: string, q: LogsQuery, format: "csv" | "json") => {
  const { cursor: _c, ...rest } = logsQuery(q);
  return buildUrl(`/projects/${enc(projectId)}/logs`, { ...rest, limit: 1000, format });
};

// ---------- Assistant (RF-CHAT-01..04) ----------

export const sendProjectMessage = (projectId: string, body: { content: string; session_id?: string; command?: AssistantCommand }, idempotencyKey: string, signal?: AbortSignal) =>
  api<AssistantMessageResponse>(`/projects/${enc(projectId)}/assistant/messages`, { method: "POST", body, idempotencyKey, ...withSignal(signal) });
export const listProjectSessions = (projectId: string, signal?: AbortSignal) => api<AssistantSessionsResponse>(`/projects/${enc(projectId)}/assistant/messages`, withSignal(signal));
export const getProjectSession = (projectId: string, sessionId: string, signal?: AbortSignal) =>
  api<AssistantHistoryResponse>(`/projects/${enc(projectId)}/assistant/messages`, { query: { session_id: sessionId }, ...withSignal(signal) });
/** Organization-level (Home) assistant: sessions are private to their owner. */
export const sendHomeMessage = (body: { content: string; session_id?: string }, idempotencyKey: string, signal?: AbortSignal) =>
  api<AssistantMessageResponse>("/assistant/messages", { method: "POST", body, idempotencyKey, ...withSignal(signal) });

// ---------- Infrastructure (RF-INFRA-01..04) ----------

export const listServers = (signal?: AbortSignal) => api<{ items: InfraServer[] }>("/infrastructure/servers", withSignal(signal));
export const createServer = (body: CreateServerInput) => api<InfraServer>("/infrastructure/servers", { method: "POST", body });
/** Network steps are BLOCKED-external in this wave: the API answers 409 policy_denied with the steps not run. */
export const validateServer = (id: string) => api<unknown>(`/infrastructure/servers/${enc(id)}/validate`, { method: "POST", body: {} });
export const bootstrapServer = (id: string) => api<unknown>(`/infrastructure/servers/${enc(id)}/bootstrap`, { method: "POST", body: {} });
export const serverAction = (id: string, body: { action: "drain" | "restart" | "repair" | "disable" | "run_command"; expected_version: number; resource?: { kind: string; id: string }; reason?: string }) =>
  api<{ server_id: string; action: string; status: string; server?: InfraServer }>(`/infrastructure/servers/${enc(id)}/actions`, { method: "POST", body });
export const listCoolify = (signal?: AbortSignal) => api<{ items: CoolifyConnection[] }>("/infrastructure/coolify-connections", withSignal(signal));
export const createCoolify = (body: CreateCoolifyInput) => api<CoolifyConnection>("/infrastructure/coolify-connections", { method: "POST", body });
export const updateServer = (id: string, body: PatchServerInput) => api<InfraServer>(`/infrastructure/servers/${enc(id)}`, { method: "PATCH", body });
/** Write-only rotation of one server secret; only its masked state returns. */
export const putServerSecret = (id: string, field: ServerSecretField, value: string) =>
  api<InfraServer>(`/infrastructure/servers/${enc(id)}/secrets/${field}`, { method: "PUT", body: { value } });
export const revokeServerSecret = (id: string, field: ServerSecretField) => api<InfraServer>(`/infrastructure/servers/${enc(id)}/secrets/${field}`, { method: "DELETE" });
export const updateCoolify = (id: string, body: PatchCoolifyInput) => api<CoolifyConnection>(`/infrastructure/coolify-connections/${enc(id)}`, { method: "PATCH", body });
export const putCoolifySecret = (id: string, field: CoolifySecretField, value: string) =>
  api<CoolifyConnection>(`/infrastructure/coolify-connections/${enc(id)}/secrets/${field}`, { method: "PUT", body: { value } });
export const revokeCoolifySecret = (id: string, field: CoolifySecretField) => api<CoolifyConnection>(`/infrastructure/coolify-connections/${enc(id)}/secrets/${field}`, { method: "DELETE" });
/** BLOCKED-external in this wave: answers 409 policy_denied with the steps not run. */
export const testCoolify = (id: string) => api<unknown>(`/infrastructure/coolify-connections/${enc(id)}/test`, { method: "POST", body: {} });
